// src/pages/Login.js
import { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import { useNavigate, Link } from 'react-router-dom';

const styles = {
  wrap: {
    minHeight: '100vh', background: '#1a1a1a',
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', padding: 20,
  },
  logo: {
    marginBottom: 24, textAlign: 'center',
  },
  logoIcon: {
    width: 64, height: 64, background: '#2271b1',
    borderRadius: 8, display: 'flex', alignItems: 'center',
    justifyContent: 'center', margin: '0 auto 12px', fontSize: 28,
  },
  logoText: { color: '#f0f0f1', fontSize: 22, fontWeight: 700, letterSpacing: '.5px' },
  logoSub: { color: '#a7aaad', fontSize: 12, marginTop: 2 },
  card: {
    background: '#232323', border: '1px solid #3a3a3a',
    borderRadius: 6, padding: '28px 32px', width: '100%', maxWidth: 360,
    boxShadow: '0 4px 24px rgba(0,0,0,.5)',
  },
  title: { color: '#f0f0f1', fontSize: 18, fontWeight: 600, marginBottom: 20, textAlign: 'center' },
  label: { display: 'block', color: '#a7aaad', fontSize: 12, fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '.5px' },
  field: { marginBottom: 16 },
  btn: {
    width: '100%', padding: '10px 0', background: '#2271b1',
    border: 'none', borderRadius: 4, color: '#fff',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    transition: 'background .15s', marginTop: 4,
  },
  err: {
    background: 'rgba(214,54,56,.12)', border: '1px solid rgba(214,54,56,.4)',
    borderLeft: '4px solid #d63638', borderRadius: 4,
    padding: '10px 14px', color: '#d63638', fontSize: 13, marginBottom: 16,
  },
  success: {
    background: 'rgba(0,163,42,.12)', border: '1px solid rgba(0,163,42,.4)',
    borderLeft: '4px solid #00a32a', borderRadius: 4,
    padding: '10px 14px', color: '#00a32a', fontSize: 13, marginBottom: 16,
  },
  foot: { textAlign: 'center', marginTop: 20, color: '#646970', fontSize: 13 },
  link: { color: '#2271b1', fontWeight: 600 },
  divider: { borderColor: '#3a3a3a', margin: '16px 0' },
};

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [pass, setPass] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const login = async (e) => {
    e.preventDefault();
    setErr(''); setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, pass);
      const snap = await getDoc(doc(db, 'admins', cred.user.uid));
      if (!snap.exists()) { setErr('Admin account পাওয়া যায়নি।'); await auth.signOut(); return; }
      const data = snap.data();
      if (data.status === 'pending') { setErr('আপনার account এখনো approve হয়নি।'); await auth.signOut(); return; }
      if (data.status === 'blocked') { setErr('আপনার account block করা হয়েছে।'); await auth.signOut(); return; }
      nav('/dashboard');
    } catch (e) {
      setErr(e.code === 'auth/invalid-credential' ? 'Email বা Password ভুল।' : 'Login ব্যর্থ হয়েছে।');
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.logo}>
        <div style={styles.logoIcon}>🚂</div>
        <div style={styles.logoText}>RailTrack</div>
        <div style={styles.logoSub}>Admin Panel</div>
      </div>
      <div style={styles.card}>
        <div style={styles.title}>Login করুন</div>
        {err && <div style={styles.err}>{err}</div>}
        <form onSubmit={login}>
          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="admin@example.com" required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Password</label>
            <input type="password" value={pass} onChange={e => setPass(e.target.value)} placeholder="••••••••" required />
          </div>
          <button style={styles.btn} disabled={loading}>{loading ? 'অপেক্ষা করুন...' : 'Login করুন →'}</button>
        </form>
      </div>
      <div style={styles.foot}>
        Admin account নেই? <Link to="/register" style={styles.link}>Register করুন</Link>
      </div>
    </div>
  );
}

export function RegisterPage() {
  const [form, setForm] = useState({ name: '', email: '', pass: '', confirm: '' });
  const [err, setErr] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const register = async (e) => {
    e.preventDefault();
    setErr(''); setSuccess('');
    if (form.pass !== form.confirm) { setErr('Password মিলছে না।'); return; }
    if (form.pass.length < 6) { setErr('Password কমপক্ষে ৬ অক্ষর।'); return; }
    setLoading(true);
    try {
      console.log('Step 1: Creating user...');
      const cred = await createUserWithEmailAndPassword(auth, form.email, form.pass);
      console.log('Step 2: User created:', cred.user.uid);

      // Token refresh
      await cred.user.getIdToken(true);
      console.log('Step 3: Token refreshed');

      // Timeout দিয়ে setDoc চালান
      const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('timeout')), 10000)
      );

      await Promise.race([
        setDoc(doc(db, 'admins', cred.user.uid), {
          uid: cred.user.uid, name: form.name, email: form.email,
          status: 'pending', role: 'admin',
          createdAt: serverTimestamp(),
        }),
        timeout
      ]);

      console.log('Step 4: setDoc done!');
      await auth.signOut();
      setSuccess('Registration সফল! Approve হওয়ার জন্য অপেক্ষা করুন।');
      setForm({ name: '', email: '', pass: '', confirm: '' });

    } catch (e) {
      console.log('Error code:', e.code);
      console.log('Error message:', e.message);
      if (e.message === 'timeout') {
        setErr('Firestore connect হচ্ছে না। Internet বা Firebase config চেক করুন।');
      } else if (e.code === 'auth/email-already-in-use') {
        setErr('এই Email দিয়ে account আছে।');
      } else {
        setErr('Registration ব্যর্থ হয়েছে: ' + e.message);
      }
      await auth.signOut();
    } finally { setLoading(false); }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.logo}>
        <div style={styles.logoIcon}>🚂</div>
        <div style={styles.logoText}>RailTrack</div>
        <div style={styles.logoSub}>Admin Registration</div>
      </div>
      <div style={{ ...styles.card, maxWidth: 400 }}>
        <div style={styles.title}>Admin Account খুলুন</div>
        {err && <div style={styles.err}>{err}</div>}
        {success && <div style={styles.success}>{success}</div>}
        <form onSubmit={register}>
          {[['name', 'নাম', 'text', 'আপনার পূর্ণ নাম'], ['email', 'Email', 'email', 'admin@example.com'], ['pass', 'Password', 'password', '••••••••'], ['confirm', 'Confirm Password', 'password', '••••••••']].map(([k, l, t, p]) => (
            <div key={k} style={styles.field}>
              <label style={styles.label}>{l}</label>
              <input type={t} value={form[k]} onChange={set(k)} placeholder={p} required />
            </div>
          ))}
          <button style={styles.btn} disabled={loading}>{loading ? 'অপেক্ষা করুন...' : 'Register করুন'}</button>
        </form>
      </div>
      <div style={styles.foot}>
        আগেই account আছে? <Link to="/login" style={styles.link}>Login করুন</Link>
      </div>
    </div>
  );
}
